import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { COLOR } from "../../../common/Color"
import { Align } from "../../../common/TextUtils"
import { GameWithScore, StudentResultsStore } from "../../../react/students/StudentResultsStore"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { ButtonSize, ButtonType } from "../../generic-sprites/Buttons"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { ArrowSprite } from "./sprites/ArrowSprite"
import { WheelOfMisfortuneButton } from "./sprites/WheelOfMisfortuneButton"
import { WheelSprite } from "./sprites/WheelSprite"
import { ScoresScreen } from "./utils/ScoresScreen"
import { ScoreText } from "./utils/ScoreText"
import { BulletedTextBoxConfig, ButtonTextBoxConfig, RegularTextBoxConfig, TextBox, TextBoxText } from "./utils/TextBox"
import { loadAssets, WomAudio, WomImage } from "./WheelOfMisfortuneAssets"

export class WheelOfMisfortuneState extends Phaser.State {
    background: Phaser.Sprite
    arrow: ArrowSprite
    wheel: WheelSprite
    sideButtons: Phaser.Group
    smokeButton: WheelOfMisfortuneButton
    stopButton: WheelOfMisfortuneButton
    scoreText: ScoreText
    private round = 1

    async preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        if (!PhaserUtils.loggedIn() && !CCUtils.debug) {
            PhaserUtils.notLoggedInMessage(this.game)
        } else {
            ColorUtils.setBackgroundColorBlack(this.game)

            this.background = Sprites.add(this.game, WomImage.BACKGROUND)
            this.sideButtons = this.game.add.group()

            const sideScaleX = Scale.x(0.735)
            const sideScaleY = Scale.y(0.75)
            const scoreTextX = Scale.x(0.88)
            const scoreTextY = Scale.y(0.105)

            this.scoreText = new ScoreText(this.game, scoreTextX, scoreTextY)
            this.wheel = new WheelSprite(this.game, this)
            this.arrow = new ArrowSprite(this.game)
            this.smokeButton = new WheelOfMisfortuneButton(
                this.game,
                "Smoke/Vape",
                this.sideButtons,
                sideScaleX,
                sideScaleY
            )
            const height = this.smokeButton.button.button.height
            this.stopButton = new WheelOfMisfortuneButton(
                this.game,
                "Stop",
                this.sideButtons,
                sideScaleX,
                sideScaleY + height + Scale.y(0.05),
                this.wheel.stopSpin
            )

            this.centerSpritesInGraphic(this.wheel, this.arrow)

            this.beginRound()
        }
    }

    beginRound = () => {
        if (this.round === 1) {
            this.firstRoundIntro()
        } else {
            this.nextRoundIntro()
        }
    }

    viewScoresScreen = () => {
        // remove cig and wheel from view
        this.wheel.hide()
        this.arrow.hide()
        this.enableSideButtons()

        const narrator = this.game.add.audio(this.getViewScoresAudio())
        narrator.play()
        const group = this.game.add.group()
        ScoresScreen.display(this.game, group)

        // change button function and title (given the round)
        this.smokeButton.setText("Play Again")

        if (this.round >= 3) {
            this.smokeButton.disable()
        } else {
            this.smokeButton.removeEvents()
            this.smokeButton.setOnClickOnce(() => {
                group.destroy(true)
                this.scoreText.reset()
                narrator.stop()
                this.wheel.view()
                this.arrow.view()
                this.wheel.resetWheel()
                this.smokeButton.setText("Smoke/Vape")
                this.stopButton.setText("Stop")
                this.stopButton.removeEvents()
                this.stopButton.setOnClick(this.wheel.stopSpin)
                this.beginNextRound()
            })
        }
        if (this.round > 1) {
            this.stopButton.setText("Exit")
            this.stopButton.setOnClickOnce(() => Transitions.startOutro(this.game, ActivityKey.WHEEL_OF_MISFORTUNE))
            this.stopButton.enable()
        } else {
            this.stopButton.disable()
        }
    }

    bonusScreen = () => {
        const group = this.game.add.group()
        const buttonY = Scale.y(0.7)
        const gameOverConfig: ButtonTextBoxConfig = {
            x: Scale.x(0.04),
            y: Scale.y(0.35),
            width: Scale.x(0.58),
            height: Scale.y(0.55),
            textConfig: {
                text: "You've been lucky so far and haven't gotten addicted. Do you want to spin again? We'll give you 50 bonus points if you land on a good" +
                    " slice this turn. ",
                config: {
                    x: Scale.x(0.08),
                    y: Scale.y(0.4),
                    group,
                    align: Align.CENTER,
                    wordWrapWidth: 0.5
                }
            },
            buttonConfigs: [
                {
                    text: "YES",
                    onClick: () => {
                        group.destroy(true)
                        this.wheel.bonus = true
                        this.wheel.playChangeAudio(this.round)
                    },
                    config: {
                        x: Scale.x(0.15),
                        y: buttonY,
                        group,
                        buttonSize: ButtonSize.WIDE,
                        buttonType: ButtonType.SECONDARY
                    }
                },
                {
                    text: "NO",
                    onClick: () => {
                        group.destroy(true)
                        this.greatChoiceScreen()

                    },
                    config: {
                        x: Scale.x(0.35),
                        y: buttonY,
                        group,
                        buttonSize: ButtonSize.WIDE,
                        buttonType: ButtonType.SECONDARY
                    },
                }
            ]
        }
        TextBox.createWithButtons(this.game, group, gameOverConfig, WomAudio.LUCKY_SO_FAR)
    }

    async gameOverScreen(playLoss = true) {
        // Add newest score to Firestore
        await StudentResultsStore.instance.addScore(GameWithScore.WHEEL_OF_MISFORTUNE, this.scoreText.score)

        const group = this.game.add.group()
        const buttonY = Scale.y(0.55)
        const gameOverConfig: ButtonTextBoxConfig = {
            x: Scale.x(0.04),
            y: Scale.y(0.35),
            width: Scale.x(0.58),
            height: Scale.y(0.55),
            textConfig: {
                text: "GAME OVER",
                config: {
                    x: Scale.x(0.09),
                    y: Scale.y(0.4),
                    group,
                    fontPx: 64,
                    color: COLOR.red,
                    weight: "bold"
                }
            },
            buttonConfigs: [
                {
                    text: "Play Again",
                    onClick: () => {
                        group.destroy(true)
                        this.scoreText.reset()
                        this.wheel.resetWheel()
                        this.beginNextRound()
                    },
                    config: {
                        x: Scale.x(0.25),
                        y: buttonY,
                        group,
                        buttonSize: ButtonSize.WIDE,
                        buttonType: ButtonType.SECONDARY,
                        enabled: this.round < 3
                    }
                },
                {
                    text: "View Scores",
                    onClick: () => {
                        group.destroy(true)
                        this.viewScoresScreen()
                    },
                    config: {
                        x: Scale.x(0.25),
                        y: buttonY + Scale.y(0.1),
                        group,
                        buttonSize: ButtonSize.WIDE,
                        buttonType: ButtonType.SECONDARY
                    },
                },
                {
                    text: "Exit",
                    onClick: () => Transitions.startOutro(this.game, ActivityKey.WHEEL_OF_MISFORTUNE),
                    config: {
                        x: Scale.x(0.25),
                        y: buttonY + Scale.y(0.2),
                        group,
                        buttonSize: ButtonSize.WIDE,
                        buttonType: ButtonType.SECONDARY,
                        enabled: this.round > 1
                    },

                }
            ]
        }
        if (playLoss) {
            const buttonArray = TextBox.createWithButtons(this.game, group, gameOverConfig, WomAudio.WAH_WAH, this.disableSideButtons,
                () => {
                    const narrator = this.game.add.audio(this.getEndAudio(this.round))
                    narrator.onPlay.addOnce(() => buttonArray.forEach(button => button.disable()))
                    narrator.onStop.addOnce(() => buttonArray.forEach(button => button.enable()))
                    narrator.play()
                })
        } else {
            const buttonArray = TextBox.createWithButtons(this.game, group, gameOverConfig, this.getEndAudio(this.round),
                () => {
                    this.disableSideButtons()
                },
                () => buttonArray.forEach(button => button.enable()))
        }

    }

    enableSideButtons = () => {
        this.smokeButton.enable()
        // this.stopButton.enable()
    }

    disableSideButtons = () => {
        this.smokeButton.disable()
        this.stopButton.disable()
    }

    private bonusPointsBox = () => {
        const group = this.game.add.group()
        const config: RegularTextBoxConfig = {
            x: Scale.x(0.2),
            y: Scale.y(0.2),
            width: Scale.x(0.25),
            height: Scale.y(0.1),
            texts: [{
                text: "+ 100 bonus points",
                config: {
                    x: Scale.x(0.21),
                    y: Scale.y(0.23),
                    group,
                    fontPx: 20,
                    weight: "bold"
                }
            }]
        }
        TextBox.create(this.game, group, config, WomAudio.CHIMES, true, true,
            () => {
                this.scoreText.score += 100
                this.gameOverScreen(false)
            })
    }

    private greatChoiceScreen = () => {
        const group = this.game.add.group()
        const config: RegularTextBoxConfig = {
            x: Scale.x(0.04),
            y: Scale.y(0.35),
            width: Scale.x(0.58),
            height: Scale.y(0.55),
            texts: [
                {
                    text: "Great Choice!",
                    config: {
                        x: Scale.x(0.06),
                        y: Scale.y(0.4),
                        group,
                        fontPx: 64,
                        color: COLOR.red,
                        weight: "bold"
                    }
                },
                {
                    text: "There’s no way to win when you take your chances with addiction. You get 100 bonus points for choosing not to smoke or vape!",
                    config: {
                        x: Scale.x(0.07),
                        y: Scale.y(0.6),
                        wordWrapWidth: 0.5,
                        group,
                        fontPx: 20,
                        align: Align.CENTER
                    }
                }
            ]
        }
        TextBox.create(this.game, group, config, WomAudio.GREAT_CHOICE, false, false,
            () => {
                this.bonusPointsBox()
                group.destroy()
            })
    }

    // Create an invisible square to center the wheel and cigarette
    private centerSpritesInGraphic(wheel: WheelSprite, arrow: ArrowSprite) {
        const graphic = this.createCenteredSquare()
        Sprites.centerSpriteInSprite(graphic, wheel.wheel)
        Sprites.centerSpriteInSprite(graphic, arrow.arrow)
        this.wheel.wheel.x = this.arrow.arrow.x
        this.wheel.wheel.y = Scale.y(0.63)
        this.arrow.arrow.y = Scale.y(0.245)
    }

    private firstRoundIntro = () => {
        const introConfig: BulletedTextBoxConfig = {
            x: Scale.x(0.04),
            y: Scale.y(0.35),
            width: Scale.x(0.58),
            height: Scale.y(0.55),
            texts: [
                new TextBoxText("On the Wheel of Misfortune, spinning is like smoking a cigarette or vaping one time.", 0),
                new TextBoxText("Click 'Smoke/Vape' to spin the wheel and 'Stop' to stop the wheel.", 11000),
                new TextBoxText("If you avoid addiction, you can earn points and keep playing.", 18000),
                new TextBoxText("If you land on 'Addicted', the game is over.", 22000),
                new TextBoxText("At the end of each game, you can compare your score with your classmates'.", 25000)
            ]
        }
        TextBox.createWithBulletedText(this.game, WomAudio.ROUND_1_INTRO, introConfig, this.disableSideButtons,
            () => {
                this.enableSideButtons()
                this.smokeButton.setOnClick(() => this.wheel.spinWheel(this.round))
            })
    }

    private nextRoundIntro = () => {
        const audio = this.game.add.audio(WomAudio.ROUND_2_INTRO)
        audio.onPlay.add(this.disableSideButtons)
        audio.onStop.add(() => {
            this.enableSideButtons()
            this.smokeButton.setOnClick(() => this.wheel.spinWheel(this.round))
        })
        audio.play()
    }

    private beginNextRound = () => {
        if (this.round <= 3) {
            this.round += 1
            this.wheel.wheel.frame = 0
            this.beginRound()
        }
    }

    private createCenteredSquare = (): Phaser.Graphics => {
        const graphic = this.game.add.graphics()
        const width = 2 / 3
        return graphic.drawRect(Scale.x(0), Scale.y(0.25), Scale.x(width), Scale.y(1))
    }

    private getViewScoresAudio = (): string => `round-${this.round}-view-scores.mp3`

    private getEndAudio = (round: number): string => `round-${round}-end.mp3`
}
